import { ILocationResponse } from '@kcftech/prometheustypescriptapiclient';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { LocationDropdownProps } from 'components/dropdown/LocationDropdown';
import { useInfiniteLocationUserDataSource } from 'custom_hooks/data_hooks/data_source/locationUserDataSource';
import { useCallback } from 'react';
import { getId } from 'utils/data';

export type UserLocationDropdownProps = Readonly<
    LocationDropdownProps & {
        /**
         * The currently selected user's Id
         */
        selectedUserId: string;
    }
>;

/**
 * Dropdown which allows selecting a location from a list of available locations.
 */
export function UserLocationDropdown({
    selectedUserId,
    className,
    value,
    onChange,
    placeholder = 'Select a location',
    corporationId,
    disabled,
}: UserLocationDropdownProps) {
    const shouldShowCorporation = corporationId === undefined || corporationId === '';
    const renderLabel: DropdownLabelRenderer<ILocationResponse> = useCallback(
        ({ value: selectedValue }) =>
            shouldShowCorporation && selectedValue
                ? `${selectedValue.Corporation.CorporationName} / ${selectedValue.Name}`
                : selectedValue?.Name ?? placeholder,
        [shouldShowCorporation, placeholder]
    );

    const renderItem: DropdownItemRenderer<ILocationResponse> = useCallback(
        ({ item }) =>
            shouldShowCorporation
                ? `${item.Corporation.CorporationName} / ${item.Name}`
                : item.Name,
        [shouldShowCorporation]
    );
    const {
        results,
        filter,
        setFilter,
        isLoading,
        hasNext,
        loadNext,
    } = useInfiniteLocationUserDataSource(selectedUserId, {
        name: 'user-location-data-source',
        defaultFilter: { corporationId },
        defaultSort: [
            { field: 'CorporationName', dir: 'asc' },
            { field: 'Name', dir: 'asc' },
        ],
    });
    const onFilterChange = (nameSearch: string) =>
        setFilter((current) => ({ ...current, name: nameSearch }));
    return (
        <Dropdown<ILocationResponse>
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            renderLabel={renderLabel}
            renderItem={renderItem}
            value={value ?? undefined}
            onChange={onChange}
            filterable={{
                filterText: filter.name ?? '',
                onChange: onFilterChange,
                placeholder,
            }}
            trigger={{ className }}
            isDisabled={disabled}
        />
    );
}
